// src/layout/MainLayout.tsx
import React, { useEffect } from 'react';
import { Box, Drawer, useTheme, useMediaQuery } from '@mui/material';
import NestedDrawerMenu from './NestedDrawerMenu';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';

interface MainLayoutProps {
  children: React.ReactNode;
  drawerOpen: boolean;
  onDrawerToggle: () => void;
}

const MainLayout = ({ children, drawerOpen, onDrawerToggle }: MainLayoutProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isAuthenticated } = useSelector((state: RootState) => state.login);

  return (
    <Box sx={{ display: 'flex', flex: 1, minHeight: 0, width: '100%' }}>
      <Drawer
        variant={isMobile ? 'temporary' : 'persistent'}
        open={drawerOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          width: isAuthenticated && drawerOpen ? 250 : 0,
          flexShrink: 0,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '& .MuiDrawer-paper': {
            display: !isAuthenticated ? 'none' : 'flex',
            width: !isAuthenticated ? 0 : 250,
            boxSizing: 'border-box',
            border: 'none',
            backgroundColor: '#ffffff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.02)',
            top: '0',
            height: 'calc(100vh)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
            backdropFilter: 'blur(10px)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '1px',
              height: '100%',
              background: 'linear-gradient(180deg, transparent 0%, rgba(0,170,254,0.1) 50%, transparent 100%)',
            }
          },
        }}
      >
        <NestedDrawerMenu open={drawerOpen} onDrawerToggle={onDrawerToggle} />
      </Drawer>
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          height: '100%',
          width: { xs: '100%', sm: `calc(100% - ${drawerOpen ? 250 : 0}px)` },
          maxWidth: '100%',
          minWidth: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          marginLeft: { sm: 0 },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          backgroundColor: '#f4f5f7',
        }}
      >
        {children}
      </Box>
    </Box >
  );
};

export default MainLayout;